import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Loading from '../../components/Loading/Loading';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import threadService from '../../services/threadService';
import { CHARACTER_THEMES, normalizeThreadTheme, useCharacterSiteTheme } from '../../constants/characterThemes';
import sheetStyles from '../ThreadDetail/ThreadDetail.module.css';
import '../ThreadDetail/themes.css';
import styles from '../CharacterTheme/CharacterTheme.module.css';

const ThreadTheme = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await threadService.getThread(slug, { page: 1, limit: 1 });
      setThread(data);
      setSelectedTheme(normalizeThreadTheme(data?.threadTheme));
    } catch (err) {
      setError('Erreur lors du chargement de la discussion');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useCharacterSiteTheme(selectedTheme);

  const canEditTheme = Boolean(thread?.permissions?.canEditTheme);
  const hasChanges = thread && selectedTheme !== normalizeThreadTheme(thread.threadTheme);

  const handleSave = async () => {
    if (!thread?.threadId) return;
    setSaving(true);
    setSaveMessage('');
    try {
      await threadService.updateThreadTheme(thread.threadId, selectedTheme);
      setSaveMessage('Thème du thread enregistré.');
      await loadData();
    } catch (err) {
      setSaveMessage(err.response?.data?.error || 'Erreur lors de l\'enregistrement du thème');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout wide>
        <Loading message="Chargement du thème..." />
      </Layout>
    );
  }

  if (error || !thread) {
    return (
      <Layout wide>
        <ErrorMessage message={error || 'Discussion introuvable'} onRetry={loadData} />
      </Layout>
    );
  }

  if (thread.isCharacterSheet) {
    return (
      <Layout wide>
        <ErrorMessage
          message="Le thème d'une fiche de personnage se gère via le thème du personnage."
          onRetry={() => navigate(`/threads/${slug}`)}
        />
      </Layout>
    );
  }

  if (!canEditTheme) {
    return (
      <Layout wide>
        <ErrorMessage
          message="Vous n'avez pas la permission de modifier le thème de cette discussion."
          onRetry={() => navigate(`/threads/${slug}`)}
        />
      </Layout>
    );
  }

  const breadcrumbItems = [
    { name: 'Accueil', url: '/', icon: 'home' },
    ...(thread.breadcrumb || []).map((item) => ({
      name: item.name,
      url: item.url,
      icon: item.icon,
    })),
    { name: 'Thème du thread', url: null },
  ];

  return (
    <Layout wide>
      <div className={styles.content}>
        <Breadcrumb items={breadcrumbItems} />

        <header className={styles.header}>
          <h1 className={styles.title}>Thème du thread</h1>
          <p className={styles.subtitle}>
            Personnalisez les couleurs et le filigrane de la discussion « {thread.title} ».
            Les posts RP conservent le thème de chaque personnage.
          </p>
        </header>

        <div className={styles.layout}>
          <aside className={styles.themePanel}>
            <h2 className={styles.themePanelTitle}>Thèmes disponibles</h2>
            <div className={styles.themeList} role="radiogroup" aria-label="Thème du thread">
              {CHARACTER_THEMES.map((theme) => (
                <label
                  key={theme.id}
                  className={`${styles.themeOption} ${selectedTheme === theme.id ? styles.themeOptionActive : ''}`}
                >
                  <input
                    type="radio"
                    name="threadTheme"
                    value={theme.id}
                    checked={selectedTheme === theme.id}
                    onChange={() => setSelectedTheme(theme.id)}
                  />
                  <span className={styles.themeOptionBody}>
                    <span className={styles.themeOptionLabel}>{theme.label}</span>
                    <span className={styles.themeOptionDescription}>{theme.description}</span>
                  </span>
                </label>
              ))}
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleSave}
                disabled={saving || !hasChanges}
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              <Link to={`/threads/${slug}`} className={styles.btnSecondary}>
                Retour à la discussion
              </Link>
              {saveMessage && (
                <p className={`${styles.message} ${saveMessage.includes('Erreur') ? styles.messageError : styles.messageSuccess}`}>
                  {saveMessage}
                </p>
              )}
            </div>
          </aside>

          <section className={styles.previewPanel} aria-label="Aperçu du thème">
            <div className={styles.previewArea}>
              <p className={styles.previewHint}>
                Aperçu de l'en-tête de la discussion. Le filigrane s'applique à toute la page.
              </p>

              <div className={sheetStyles.threadHeader}>
                <div className={sheetStyles.threadHeaderContent}>
                  <div className={sheetStyles.threadHeaderMain}>
                    <div className={sheetStyles.threadTitleRow}>
                      <h2 className={sheetStyles.threadTitle}>{thread.title}</h2>
                      <div className={sheetStyles.threadBadges}>
                        {thread.sticky && (
                          <span className={`${sheetStyles.badge} ${sheetStyles.badgePinned}`}>Épinglé</span>
                        )}
                        {thread.isRoleplay && (
                          <span className={`${sheetStyles.badge} ${sheetStyles.badgeRpInProgress}`}>En cours</span>
                        )}
                      </div>
                    </div>
                    <div className={sheetStyles.threadMeta}>
                      <span className={sheetStyles.authorName}>{thread.author || 'Auteur'}</span>
                    </div>
                  </div>
                  <div className={sheetStyles.forumBadge}>
                    <span>{thread.forum?.name || 'Forum'}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default ThreadTheme;
